import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react"
import { Box, Table, TableBody } from "@mui/material"
import {
  EmptyPlaceholder,
  TableHeadComponent,
  TableRowComponent
} from "./components"
import { useStyles } from "./styles"
import { Columns, SortTypes } from "./types"
import { useSorting, useScrollToBottom } from "./hooks"
import { defaultSorter } from "./helpers"

type SelectionProps = {
  selected?: string[]
  allSelected?: boolean
  onToggle?: (id: string) => void
  onToggleAll?: (selectAll: boolean) => void
  onDeselectAll?: () => void
}

type SortProps<T = any> = {
  sortBy?: string
  order?: SortTypes
  onSort?: (sortBy: string, order: SortTypes) => void
}

type Props<T> = SelectionProps &
  SortProps<T> & {
    headerColumns: Columns[]
    displayedProperties?: string[]
    defaultDisplayedProperties: string[]
    allDisplayedProperties?: string[]
    records: T[]
    stickyHeader?: boolean
    loading?: boolean
    loadingComponent?: React.ReactNode
    emptyPlaceholder?: boolean
    emptyPlaceholderText?: string
    className?: string
    dataAutomationId?: string
    disableCheckbox?: boolean
    renderPlaceholder?: (columns: number) => React.ReactNode
    onChangeDisplayedProperties?: (displayedProperties?: string[]) => void
    onRowClick?: (id: string) => void
    getRowAction?: (id: string) => ReactElement
    isSelectionDisabled?: (record: T) => boolean
    children?: (prop: string, record: T) => React.ReactNode
    onScrollToBottom?: () => void
    ariaLabelledBy?: string
    ariaLabel?: string
  }

const DataTable = <T extends { id: string }>({
  selected,
  allSelected,
  headerColumns,
  displayedProperties: outerDisplayedProperties,
  defaultDisplayedProperties,
  allDisplayedProperties,
  records = [],
  stickyHeader = false,
  loading,
  loadingComponent,
  emptyPlaceholder = true,
  emptyPlaceholderText,
  disableCheckbox = false,
  className,
  sortBy: currentSortBy,
  order: currentOrder,
  onSort: handleSort,
  onChangeDisplayedProperties,
  renderPlaceholder,
  onToggle,
  onToggleAll,
  onRowClick,
  getRowAction,
  isSelectionDisabled,
  children,
  dataAutomationId,
  onScrollToBottom,
  ariaLabelledBy,
  ariaLabel
}: React.PropsWithChildren<Props<T>>) => {
  const { classes, cx } = useStyles()
  const tableWrapperRef = useScrollToBottom(onScrollToBottom)
  const internalExitRef = useRef<HTMLDivElement>(null)

  const [displayedProperties, setDisplayedProperties] = useState<string[]>(
    outerDisplayedProperties ?? defaultDisplayedProperties
  )

  useEffect(() => {
    if (outerDisplayedProperties) {
      setDisplayedProperties(outerDisplayedProperties)
    }
  }, [outerDisplayedProperties])

  // REVIEW: Remove sort state from DataTable completely and handle outside?
  const { sortBy, order, onSort } = useSorting(
    currentSortBy,
    currentOrder,
    handleSort
  )
  const displayRecords = handleSort
    ? records
    : [...records].sort(defaultSorter(sortBy, order))

  const selectDisplayedProperties = useCallback(
    (selectedPropId: string): void => {
      if (displayedProperties.includes(selectedPropId)) {
        if (displayedProperties.length === 1) return

        setDisplayedProperties(prevState => {
          const newDisplayedProperties = prevState.filter(
            propId => propId !== selectedPropId
          )
          onChangeDisplayedProperties?.(newDisplayedProperties)

          return newDisplayedProperties
        })
        return
      }

      setDisplayedProperties(prevState => {
        const newDisplayedProperties = [...prevState, selectedPropId]
        onChangeDisplayedProperties?.(newDisplayedProperties)

        return newDisplayedProperties
      })
    },
    [displayedProperties, onChangeDisplayedProperties]
  )

  const totalColumnsCount =
    displayedProperties.length + 1 + Number(!!getRowAction)

  return (
    <>
      <Box
        ref={tableWrapperRef}
        className={cx(
          classes.tableWrapper,
          {
            [classes.tableWrapperLoading]: loading
          },
          className
        )}
        onKeyDown={e => {
          if (e.key === "Escape") {
            e.preventDefault()
            const target = internalExitRef.current
            target && target.focus()
          }
        }}
      >
        {loading && loadingComponent ? (
          loadingComponent
        ) : (
          <Table
            className={classes.table}
            stickyHeader={stickyHeader}
            data-automation-id={dataAutomationId}
            aria-labelledby={!ariaLabel ? ariaLabelledBy : undefined}
            aria-label={ariaLabel}
            tabIndex={0}
          >
            <TableHeadComponent
              selected={allSelected}
              headerColumns={headerColumns}
              displayedProperties={displayedProperties}
              allDisplayedProperties={allDisplayedProperties}
              disableCheckbox={disableCheckbox}
              sortBy={sortBy}
              order={order}
              onSort={onSort}
              onSelectAllRows={onToggleAll}
              getRowAction={getRowAction}
              selectDisplayedProperties={selectDisplayedProperties}
            />
            <TableBody>
              {/* Don't sort records inside table if sort handler provided. */}
              {/* If sort handler is not provided, use default sorter. */}
              {displayRecords.map(record => {
                return (
                  <TableRowComponent<T>
                    key={record.id}
                    record={record}
                    selectedRows={selected}
                    displayedProperties={displayedProperties}
                    allDisplayedProperties={allDisplayedProperties}
                    headerColumns={headerColumns}
                    disableCheckbox={disableCheckbox}
                    onClick={onRowClick}
                    onSelectRow={onToggle}
                    getRowAction={getRowAction}
                    disableSelect={isSelectionDisabled?.(record) ?? false}
                  >
                    {children}
                  </TableRowComponent>
                )
              })}
              {!records.length &&
                emptyPlaceholder &&
                (renderPlaceholder?.(totalColumnsCount) ?? (
                  <EmptyPlaceholder
                    columns={totalColumnsCount}
                    text={emptyPlaceholderText}
                  />
                ))}
            </TableBody>
          </Table>
        )}
      </Box>
      <Box ref={internalExitRef} tabIndex={-1} aria-label="End of table" />
    </>
  )
}

export default React.memo(DataTable)
